import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import Tooltip from '../../components/Tooltip';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import topicPath from '../../utils/url/topicPath';
import ContentMapColumnHeader from '../ContentMapColumnHeader';
import TopicNameWithIcon from '../TopicNameWithIcon';
import useTopicDrop from '../useTopicDrop';
import * as styles from './TopicTreeCard.module.scss';
import TopicTreeCardDropSeparatorStack from './TopicTreeCardDropSeparatorStack';
import TopicTreeCardItem from './TopicTreeCardItem';
import TopicTreeCardItemDraggable from './TopicTreeCardItemDraggable';
import TopicTreeCardMenu from './TopicTreeCardMenu';
import type { TopicTreeCardMenuCallbacks } from './TopicTreeCardMenu';
import TopicTreeCardPost from './TopicTreeCardPost';
import type { TopicTreeCardPostCallbacks } from './TopicTreeCardPost';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated';
import { PostLinkAccess, PostType } from '../../graphql/generated/graphql';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import { DragTypes, MoveStates, TopicStatus } from '../constants';
import type {
  ReorganizationPostEdge,
  SelectedPosts,
  TopicReorganizationMetadata,
  TopicReorganizationTree,
} from '../useTopicReorganization';
import isReorganizationPostEdgePinned from '../utils/isReorganizationPostEdgePinned';
import type { ContentMapGetDropParams } from '../utils/types';
import useStayInViewport from '../utils/useStayInViewport';

const POST_FRAGMENT = gql(`
  fragment TopicTreeCard_postsMap on Post {
    id
    title
    ...TopicTreeCardPost_post
  }
`);

const CARD_LEVEL_INDENT = parseInt(styles.cardLevelIndent, 10);
const POST_HEIGHT = parseInt(styles.postHeight, 10);
const POST_LIST_BOTTOM_SPACING = parseInt(styles.postListBottomSpacing, 10);
const TOPIC_HEIGHT = parseInt(styles.topicHeight, 10);

export const getCardHeight = (postCount: number) =>
  TOPIC_HEIGHT +
  postCount * POST_HEIGHT +
  (postCount > 0 ? POST_LIST_BOTTOM_SPACING : 0);

const getRowKey = ({
  postKey,
  reorganizationMetadata,
}: ReorganizationPostEdge) => {
  const { move, isPinned } = reorganizationMetadata;
  const rowKey = [postKey, move.state].join('-');

  // Toggling pinned status should re-create the component,
  // otherwise React DnD's item type won't change:
  // https://github.com/react-dnd/react-dnd/issues/1860
  if (typeof isPinned !== 'boolean') return rowKey;
  return `${rowKey}-${isPinned ? 'pinned' : 'unpinned'}`;
};

export interface TopicTreeCardCallbacks
  extends TopicTreeCardMenuCallbacks,
    TopicTreeCardPostCallbacks {
  onTopicSelectedChange: (params: { id: string; selected: boolean }) => void;
  onTopicNameChange: (params: { id: string; name: string | null }) => void;
}

interface TopicTreeCardProps extends TopicTreeCardCallbacks {
  header: boolean;
  specialColumn: 'posts' | 'unorganized' | null;
  tree: TopicReorganizationTree;
  showPosts: boolean;
  reorganizable: boolean;
  isReorganizing: boolean;
  getDropParams: ContentMapGetDropParams;
  columnSelected: TopicReorganizationTree['selected'];
  ancestorsReorganizationMetadata: TopicReorganizationMetadata[];
  selectedPosts: SelectedPosts;
  onDragOverChange: (
    over: boolean,
    data: { topicId: string; key: string },
  ) => void;
  dragOverTopicId?: string;
  expanded: boolean;
  initialRenaming?: boolean;
  loadPost: (id: string) => void;
  postsMap: Record<string, DocumentType<typeof POST_FRAGMENT>>;
}

const TopicTreeCard = ({
  header,
  specialColumn,
  showPosts,
  tree,
  reorganizable,
  isReorganizing,
  columnSelected,
  getDropParams,
  onTopicSelectedChange,
  onTopicDeletedChange,
  onTopicNameChange,
  onSubtopicCreate,
  onPostPinnedChange,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostArchivedChange,
  ancestorsReorganizationMetadata,
  selectedPosts,
  onDragOverChange,
  dragOverTopicId,
  expanded,
  onExpandedChange,
  initialRenaming,
  loadPost,
  postsMap,
}: TopicTreeCardProps) => {
  let accept = [
    DragTypes.column,
    DragTypes.mixed,
    DragTypes.pinnedPosts,
    DragTypes.posts,
    DragTypes.topics,
  ];
  if (specialColumn === 'posts') {
    accept = [DragTypes.pinnedPosts, DragTypes.posts];
  } else if (specialColumn === 'unorganized') {
    accept = [];
  }
  const dropRef = useTopicDrop(accept, {
    tree,
    getDropParams,
    onDragOverChange,
    topicId: tree.topic.id,
  });

  const { reorganizationMetadata, reorganizationTopicPosts } = tree;

  const [isRenaming, setIsRenaming] = useState(
    initialRenaming ||
      (reorganizationMetadata.isNew && !reorganizationMetadata.name),
  );
  const [newName, setNewName] = useState('');
  const hasSubtopics = tree.children.length > 0;

  const isMovedOut = reorganizationMetadata.move.state === MoveStates.movedOut;
  const isDeleted =
    !isMovedOut && reorganizationMetadata.status === TopicStatus.deleted;
  const name = isMovedOut
    ? tree.topic.name
    : reorganizationMetadata.name || tree.topic.name;

  const cancelRenaming = () => {
    setIsRenaming(false);
    setNewName('');
  };

  const saveTopicName = () => {
    onTopicNameChange({
      id: tree.topic.id,
      name: newName === tree.topic.name ? null : newName,
    });
    cancelRenaming();
  };

  const allSlimPostsMap = useAllSlimPostMap();

  const postAndEdges = useMemo(() => {
    if (reorganizationTopicPosts.status !== 'loaded') return [];
    return reorganizationTopicPosts.edges
      .map((reorganizationPostEdge) => {
        const slimPost = allSlimPostsMap[reorganizationPostEdge.postId];
        const post = postsMap[reorganizationPostEdge.postId] ?? {
          id: reorganizationPostEdge.postId,
          title: slimPost?.title ?? '',
          publishedAt: slimPost?.publishedAt ?? null,
          linkAccess: slimPost?.linkAccess ?? PostLinkAccess.Internal,
          archivedAt: slimPost?.archivedAt ?? null,
          verifiedAt: slimPost?.verifiedAt ?? null,
          type: slimPost?.type ?? PostType.Post,
        };
        return { post, reorganizationPostEdge };
      })
      .toSorted((a, b) => {
        const isAPinned = isReorganizationPostEdgePinned(
          a.reorganizationPostEdge,
        );
        const isBPinned = isReorganizationPostEdgePinned(
          b.reorganizationPostEdge,
        );
        // Respect the original order when two posts are both pinned
        if (isAPinned && isBPinned) return 0;
        if (isAPinned && !isBPinned) return -1;
        if (!isAPinned && isBPinned) return 1;
        return a.post.title.localeCompare(b.post.title);
      });
  }, [reorganizationTopicPosts, allSlimPostsMap, postsMap]);

  const getPropsForStayInViewport = useStayInViewport((ids) => {
    ids.forEach((id) => loadPost(id));
  });

  let cardContentInner = null;
  const hasPosts =
    showPosts &&
    (postAndEdges.length > 0 ||
      (reorganizationTopicPosts.status === 'cached' &&
        reorganizationTopicPosts.postIds.length > 0));
  if (expanded && hasPosts) {
    const lastPinnedPostIndex = _.findLastIndex(
      postAndEdges.map(({ reorganizationPostEdge }) => reorganizationPostEdge),
      isReorganizationPostEdgePinned,
    );

    cardContentInner = (
      <div className={styles.postList}>
        {postAndEdges.map(({ post, reorganizationPostEdge }, index) => (
          <div
            key={getRowKey(reorganizationPostEdge)}
            {...getPropsForStayInViewport(reorganizationPostEdge.postId)}
          >
            <TopicTreeCardPost
              reorganizable={reorganizable}
              ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
              reorganizationPostEdge={reorganizationPostEdge}
              post={post}
              index={index}
              lastPinnedPostIndex={lastPinnedPostIndex}
              tree={tree}
              isReorganizing={isReorganizing}
              columnSelected={columnSelected}
              selectedPosts={selectedPosts}
              getDropParams={getDropParams}
              onPostSelectedChange={onPostSelectedChange}
              onPostDeletedChange={onPostDeletedChange}
              onPostPinnedChange={onPostPinnedChange}
              onPostArchivedChange={onPostArchivedChange}
            />
          </div>
        ))}
        {reorganizationTopicPosts.status === 'cached' &&
          reorganizationTopicPosts.postIds.map((value) => (
            <div className={styles.postPlaceholder} key={value} />
          ))}
      </div>
    );
  }

  let topicContent = null;
  if (isRenaming) {
    topicContent = (
      <ClickOutsideHandler onClickOutside={cancelRenaming}>
        <form
          onSubmit={(e) => {
            saveTopicName();
            e.preventDefault();
          }}
          className={styles.renameForm}
        >
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            type="text"
            placeholder={name}
            onKeyDown={({ key }) => {
              if (key === 'Escape') cancelRenaming();
            }}
            aria-label="Rename topic"
          />
          <Button
            variant="primary"
            disabled={!newName}
            size="small"
            onClick={saveTopicName}
          >
            Save
          </Button>
        </form>
      </ClickOutsideHandler>
    );
  } else {
    const topicTitle = (
      <div className={styles.title}>
        {header ? (
          <ContentMapColumnHeader
            movedIn={reorganizationMetadata.move.state === MoveStates.movedIn}
            topic={tree.topic}
            name={name}
          />
        ) : (
          <TopicNameWithIcon topic={tree.topic} name={name} truncate="single" />
        )}
      </div>
    );
    topicContent = specialColumn ? (
      <div className={styles.nonDraggableTitle}>{topicTitle}</div>
    ) : (
      <TopicTreeCardItemDraggable
        type="topic"
        to={topicPath(tree.topic, 'subtopics')}
        onCheckboxChange={() =>
          onTopicSelectedChange({
            id: tree.topic.id,
            selected: !tree.isSelected,
          })
        }
        reorganizable={reorganizable}
        isChecked={tree.isSelected}
        isCheckboxDisabled={tree.isParentSelected}
        isReorganizing={isReorganizing}
        isMovedOut={isMovedOut}
        toMove={
          tree.isSelected
            ? columnSelected
            : {
                postKeys: [],
                pinnedPostKeys: [],
                topicIds: [tree.topic.id],
              }
        }
      >
        {topicTitle}
      </TopicTreeCardItemDraggable>
    );
  }

  const cardContent = (
    <div
      className={classNames(styles.cardContentInner, {
        [styles.disabled]: !reorganizable && isReorganizing,
      })}
    >
      <TopicTreeCardItem
        type="topic"
        reorganizationMetadata={reorganizationMetadata}
        ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
        originalName={tree.topic.name}
        disableTooltip={isRenaming}
        titleLine={
          <div
            className={classNames(styles.topicLine, {
              [styles.deleted]: isDeleted,
            })}
          >
            <div className={styles.topic}>{topicContent}</div>
          </div>
        }
        controls={
          !header &&
          !isRenaming &&
          (hasSubtopics || hasPosts) && (
            <Button
              onClick={() => onExpandedChange(false)}
              className={styles.expandButton}
              block
            >
              <Caret direction={expanded ? 'up' : 'down'} size={10} />
            </Button>
          )
        }
        menu={
          !isRenaming &&
          !specialColumn &&
          !isMovedOut && (
            <TopicTreeCardMenu
              isReorganizing={isReorganizing}
              deleted={isDeleted}
              header={header}
              topic={tree.topic}
              hasSubtopics={hasSubtopics}
              hasPosts={hasPosts}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onSubtopicCreate={onSubtopicCreate}
              onTopicDeletedChange={onTopicDeletedChange}
              onRenameButtonClick={() => {
                setIsRenaming(true);
                setNewName(name);
              }}
            />
          )
        }
      >
        {cardContentInner}
      </TopicTreeCardItem>
    </div>
  );

  return (
    <div>
      <div ref={reorganizable ? dropRef : undefined}>
        <div
          className={classNames(styles.card, {
            [styles.isDragOver]:
              !isMovedOut && dragOverTopicId === tree.topic.id,
          })}
          style={{
            marginLeft:
              CARD_LEVEL_INDENT * ancestorsReorganizationMetadata.length,
          }}
        >
          <div className={styles.cardContent}>
            {!isReorganizing || reorganizable ? (
              cardContent
            ) : (
              <Tooltip
                trigger="clickOrHover"
                display="block"
                content={`You must be a\ntopic owner to take\naction on this topic\nor its posts`}
              >
                {cardContent}
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {isReorganizing && (
        <TopicTreeCardDropSeparatorStack
          tree={tree}
          expanded={expanded}
          getDropParams={getDropParams}
        />
      )}
    </div>
  );
};

export default TopicTreeCard;
